import { goTo } from '@adornis/router/client/open-href.js';

import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-accordeon.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/popover/x-dropdown.js';
import '@ils/osces/client/ils-osce-overview.js';
import '@ils/osces/client/ils-osce-type.js';
import { Slot } from '@ils/slots/db/slot.js';
import { UserRole } from '@ils/static/enums.js';
import { html, noChange, nothing } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { customElement } from 'lit/decorators.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { DateTime } from 'luxon';
import { distinctUntilChanged, filter, switchMap } from 'rxjs';
import { ExamStateEnum, type ExamState } from '../examTypes.js';
import './ils-progress-button.js';

@customElement('ils-exam-sidebar')
export class ILSExamSidebar extends ChemistryLitElement {
  override styles() {
    return [{ ':host': { display: 'flex', flexDirection: 'column' } }];
  }
  @property() examState = new RXController<ExamState | undefined>(this, undefined);

  @state() private readonly _currentSlot = new RXController(
    this,
    this.examState.observable.pipe(
      filter(state => !!state?.slot?._id),
      distinctUntilChanged(
        (prevState, currState) => !!currState?.slot?._id && prevState?.slot?._id === currState.slot._id,
      ),
      switchMap(state =>
        getByID(
          Slot,
          state!.slot!._id,
        )({
          osce: { osceType: 1, titleTrainee: 1, taskType: 1, files: 1, enableVideo: 1, conversationType: 1 },
          templatedOSCEtext: {
            sceneDescriptionTester: 1,
            sceneDescriptionTrainee: 1,
            taskDescriptionTrainee: 1,
            shortDescription: 1,
          },
          recordAudio: 1,
        }),
      ),
    ),
  );

  // eslint-disable-next-line complexity
  override render() {
    if (!this.examState.value) return noChange;
    if (this.examState.value.state === ExamStateEnum.FINISHED) return this.endScreen();
    if (this.examState.value.state === ExamStateEnum.PAUSE) return this.transition();
    if (this._currentSlot.isLoading)
      return html`
        <x-flex crossaxis-center center ${css({ height: '100%', width: '100%' })}> <x-loader></x-loader> </x-flex>
      `;
    if (!this._currentSlot.value)
      return html`
        <x-empty-placeholder
          .text=${'Der aktuelle Slot konnte nicht gefunden werden'}
          .img=${'close'}
        ></x-empty-placeholder>
      `;
    const isTester = this.examState.value.userRole === UserRole.TESTER;

    return html`
      <x-flex ${css({ height: '100%' })}>
        <x-flex id="heading" padding="md" crossaxis-center ${css({ background: this.colors.neutral.shade(100) })}>
          <x-h3> Prüfung: ${this._currentSlot.value.osce?.titleTrainee ?? 'Lade...'} </x-h3>
        </x-flex>
        <x-flex
          flex
          space="md"
          padding="lg"
          ${css({
            overflowY: 'auto',
            background: this.colors.neutral.shade(100),
          })}
        >
          <x-h4> Fallszenario </x-h4>
          <x-text>
            ${unsafeHTML(
              isTester
                ? this._currentSlot.value.templatedOSCEtext?.sceneDescriptionTester
                : this._currentSlot.value.templatedOSCEtext?.sceneDescriptionTrainee,
            )}
          </x-text>
          ${!isTester || !this._currentSlot.value.recordAudio
            ? html`
                <x-h4> Aufgabenstellung </x-h4>
                <x-text> ${unsafeHTML(this._currentSlot.value.templatedOSCEtext?.taskDescriptionTrainee)} </x-text>
              `
            : nothing}
          <div flex></div>
        </x-flex>
      </x-flex>
    `;
  }

  private transition() {
    if (!this._currentSlot.value) return nothing;
    return html`
      <x-flex ${css({ height: '100%' })}>
        <x-flex id="heading" padding="md" crossaxis-center ${css({ background: this.colors.neutral.shade(100) })}>
          <x-h3> Pause </x-h3>
        </x-flex>
        <x-flex
          space="md"
          padding="md"
          ${css({
            overflowY: 'auto',
            background: this.colors.neutral.shade(100),
          })}
        >
          <x-text bold> Zeit bis Start der Situation: ${this.examState.value!.topTimer.toFormat('mm:ss')} </x-text>
          <x-text>
            ${unsafeHTML(
              this.examState.value!.userRole === UserRole.TESTER
                ? this._currentSlot.value.templatedOSCEtext?.sceneDescriptionTester
                : this._currentSlot.value.templatedOSCEtext?.shortDescription,
            )}
          </x-text>
        </x-flex>
      </x-flex>
    `;
  }

  private endScreen() {
    const state = this.examState.value!;
    let progress: number;

    if (!state.lastSlot) progress = 0;
    else {
      const buttonDuration = 20;
      const endTime = DateTime.fromJSDate(state.lastSlot.endTime!);
      const timeDiff = DateTime.now().diff(endTime).as('seconds');
      progress = Math.max(timeDiff, 0) / buttonDuration;
    }

    return html`
      <x-flex>
        <x-flex id="heading" padding="md" crossaxis-center ${css({ background: this.colors.neutral.shade(100) })}>
          <x-h3> Termin beendet </x-h3>
        </x-flex>
        <x-flex space="md" padding="lg" ${css({ overflowY: 'auto', background: this.colors.neutral.shade(100) })}>
          <x-h4> Gratulation! </x-h4>
          ${this.examState.value!.userRole === UserRole.TRAINEE
            ? html`
                <x-text> Ihr Termin ist beendet. </x-text>
                ${!this.examState.value!.lastSlot?.recordAudio
                  ? html`
                      <x-h4> Vielen Dank für Ihre Teilnahme an diesem Termin. </x-h4>
                      <x-button href="/start"> Zurück zur Übersicht </x-button>
                    `
                  : html` <x-h4> Ihre Ergebnisse erfahren sie in Kürze, bitte begeben Sie sich zum Empfang. </x-h4> `}
              `
            : !this.examState.value!.lastSlot?.recordAudio
            ? html`
                <x-text> Der Termin ist beendet </x-text> <x-button href="/start"> Zurück zur Übersicht </x-button>
              `
            : html` <x-text> Alle Situationen wurden bearbeitet </x-text> `}
        </x-flex>
        ${this.examState.value!.userRole !== UserRole.TRAINEE || !this.examState.value!.lastSlot?.recordAudio
          ? nothing
          : html`
              <x-flex padding="lg" ${css({ gridColumn: '1 / 3', background: this.colors.neutral.shade(100) })}>
                <ils-progress-button
                  .progress=${progress}
                  ?disabled=${progress < 1}
                  @click=${() => {
                    try {
                      window.history.back();
                    } catch {
                      goTo('/token');
                    }
                  }}
                >
                  Zur nächsten Prüfung
                </ils-progress-button>
              </x-flex>
            `}
      </x-flex>
    `;
  }
}
